import { exportSkinPng } from "./png";
import type { SkinDocument } from "./types";

const ZIP_UTF8_FLAG = 0x0800;
const ZIP_STORED_METHOD = 0;
const DOS_EPOCH_DATE = 33;
const textEncoder = new TextEncoder();

export async function exportBedrockSkinPack(
  document: SkinDocument,
  packName = "SkinPilot Skin",
): Promise<Blob> {
  const skinPng = new Uint8Array(
    await (await exportSkinPng(document)).arrayBuffer(),
  );
  const serializeName = "skinpilot";
  const skinName = "skin";
  const manifest = {
    format_version: 2,
    header: {
      name: packName,
      uuid: createUuid(),
      version: [1, 0, 0],
    },
    modules: [
      {
        type: "skin_pack",
        uuid: createUuid(),
        version: [1, 0, 0],
      },
    ],
  };
  const skins = {
    serialize_name: serializeName,
    localization_name: "SkinPilot",
    skins: [
      {
        localization_name: "Skin",
        geometry:
          document.model === "slim"
            ? "geometry.humanoid.customSlim"
            : "geometry.humanoid.custom",
        texture: "skin.png",
        type: "free",
      },
    ],
  };

  const files = [
    jsonFile("manifest.json", manifest),
    jsonFile("skins.json", skins),
    {
      name: "texts/languages.json",
      bytes: textEncoder.encode(`${JSON.stringify(["en_US"], null, 2)}\n`),
    },
    {
      name: "texts/en_US.lang",
      bytes: textEncoder.encode(
        `skinpack.${serializeName}=${packName}\nskin.${serializeName}.${skinName}=${packName}\n`,
      ),
    },
    { name: "skin.png", bytes: skinPng },
  ];

  return new Blob([toArrayBuffer(createStoredZip(files))], {
    type: "application/zip",
  });
}

function jsonFile(name: string, value: unknown) {
  return {
    name,
    bytes: textEncoder.encode(`${JSON.stringify(value, null, 2)}\n`),
  };
}

function createUuid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10).join(""),
  ].join("-");
}

function createStoredZip(
  files: ReadonlyArray<{ name: string; bytes: Uint8Array }>,
): Uint8Array {
  const locals: Uint8Array[] = [];
  const centralEntries: Uint8Array[] = [];
  let localOffset = 0;

  for (const file of files) {
    const name = textEncoder.encode(file.name);
    const checksum = crc32(file.bytes);
    const local = new Uint8Array(30 + name.length + file.bytes.length);
    const localView = new DataView(local.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, ZIP_UTF8_FLAG, true);
    localView.setUint16(8, ZIP_STORED_METHOD, true);
    localView.setUint16(10, 0, true);
    localView.setUint16(12, DOS_EPOCH_DATE, true);
    localView.setUint32(14, checksum, true);
    localView.setUint32(18, file.bytes.length, true);
    localView.setUint32(22, file.bytes.length, true);
    localView.setUint16(26, name.length, true);
    localView.setUint16(28, 0, true);
    local.set(name, 30);
    local.set(file.bytes, 30 + name.length);
    locals.push(local);

    const central = new Uint8Array(46 + name.length);
    const centralView = new DataView(central.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, ZIP_UTF8_FLAG, true);
    centralView.setUint16(10, ZIP_STORED_METHOD, true);
    centralView.setUint16(12, 0, true);
    centralView.setUint16(14, DOS_EPOCH_DATE, true);
    centralView.setUint32(16, checksum, true);
    centralView.setUint32(20, file.bytes.length, true);
    centralView.setUint32(24, file.bytes.length, true);
    centralView.setUint16(28, name.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, localOffset, true);
    central.set(name, 46);
    centralEntries.push(central);
    localOffset += local.length;
  }

  const centralDirectory = concatBytes(...centralEntries);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(4, 0, true);
  endView.setUint16(6, 0, true);
  endView.setUint16(8, files.length, true);
  endView.setUint16(10, files.length, true);
  endView.setUint32(12, centralDirectory.length, true);
  endView.setUint32(16, localOffset, true);
  endView.setUint16(20, 0, true);

  return concatBytes(...locals, centralDirectory, end);
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1)
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function concatBytes(...arrays: readonly Uint8Array[]): Uint8Array {
  const output = new Uint8Array(
    arrays.reduce((total, array) => total + array.length, 0),
  );
  let offset = 0;
  for (const array of arrays) {
    output.set(array, offset);
    offset += array.length;
  }
  return output;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}
